import { AxiosError } from 'axios';
import { useRef, useState } from 'react';

import {
	Box,
	Button,
	CloseButton,
	Container,
	FileButton,
	Flex,
	Group,
	Image,
	ScrollArea,
	Text,
} from '@mantine/core';
import { useForm } from '@mantine/form';

import { useMutation } from '@tanstack/react-query';

import { supportApi } from '@/shared/api/support';
import { useShowNotifaction } from '@/shared/hooks/useShowNotifaction';
import useAppStore from '@/shared/store/app.store.ts';
import { IRequestError } from '@/shared/types/app.types';
import { BottomSheet } from '@/shared/uikit/components/bottomSheet';
import { LabelInput } from '@/shared/uikit/components/input';
import { ModalSheet } from '@/shared/uikit/components/modalSheet';

import { IconPaperclip } from '@tabler/icons-react';

type TSupportFormProps = {
	opened: boolean;
	close: () => void;
};

type TSupportFormData = {
	subject: string;
	description: string;
	files?: File[];
};

interface PreviewType {
	img: string | ArrayBuffer | null;
	lastModified: number;
	name: string;
}

export const SupportForm = ({ opened, close }: TSupportFormProps) => {
	const isMobile = useAppStore((state) => state.isMobile);
	const form = useForm<TSupportFormData>({
		initialValues: {
			subject: '',
			description: '',
			files: [],
		},
		validate: {
			subject: (value) => (!value ? 'Необходимо заполнить тему' : null),
			description: (value) => (!value ? 'Необходимо описание' : null),
		},
	});

	const [previews, setPreviews] = useState<PreviewType[]>([]);

	const resetRef = useRef<() => void>(null);

	const showPreviews = (files: File[]) => {
		const previews: PreviewType[] = [];

		files.forEach((elem) => {
			previews.push({
				img: URL.createObjectURL(elem),
				lastModified: elem.lastModified,
				name: elem.name,
			});
		});

		setPreviews(previews);
	};

	const removeImage = (lastModified: number) => {
		const filteredFiles: File[] = [];

		form.values.files?.forEach((elem) => {
			if (elem.lastModified !== lastModified) filteredFiles.push(elem);
		});

		form.getInputProps('files').onChange(filteredFiles);
		showPreviews(filteredFiles);
	};

	const changeHandler = (files: File[]) => {
		let filteredFiles: File[] = [];

		resetRef.current?.();

		filteredFiles = files.filter((elem) => {
			if (form.values.files?.some((el) => el.lastModified === elem.lastModified)) return false;

			return true;
		});

		if (form.values.files) {
			form.getInputProps('files').onChange([...form.values.files, ...filteredFiles]);
			showPreviews([...form.values.files, ...filteredFiles]);
		}
	};

	const { mutate, isPending } = useMutation({
		mutationFn: supportApi.sendTicketToSupport,
		onSuccess: () => {
			void useShowNotifaction({ text: 'Обращение в поддержку отправлено', type: 'success' });
			handleResetForm();
		},
		onError: (error: AxiosError<IRequestError>) => {
			void useShowNotifaction({ text: error.response?.data?.message ?? '', type: 'error' });
		},
	});

	const handleResetForm = () => {
		form.reset();
		close();
	};

	const buttons = [
		{
			children: 'Отправить',
			...(form.isValid()
				? {
						loading: isPending,
						onClick: form.onSubmit((values) => {
							const formData = new FormData();

							Object.entries(values).forEach(([key, value]) => {
								if (Array.isArray(value)) {
									value?.length > 0 &&
										value.forEach((file, idx) => {
											formData.set(`${Date.now()}-${idx}`, file);
										});
									return;
								}

								formData.set(`${key}`, value);
							});

							mutate(formData);
						}),
					}
				: { variant: 'light' }),
		},
	];

	const content = (
		<Container p={0} pb="md">
			<form>
				<Group>
					<LabelInput
						name="subject"
						label="Тема обращения"
						placeholder="Проблема с ..."
						{...form.getInputProps('subject')}
					/>

					<LabelInput
						name="description"
						label="Описание"
						placeholder="Когда я ... не работает ..."
						{...form.getInputProps('description')}
						minRows={6}
						maxRows={6}
						multiple
					/>

					{
						<ScrollArea.Autosize mah={160} w="100%">
							<Flex gap="xs" wrap="wrap" pr={15}>
								{previews.map((elem) => {
									return (
										<Box w={70} h={70} mt={5} pos="relative" key={elem.lastModified}>
											<Image h="100%" src={elem.img} radius={10}></Image>
											<CloseButton
												pos="absolute"
												size="xs"
												right={-5}
												top={-5}
												bg="#F3F4F8"
												onClick={() => removeImage(elem.lastModified)}
											/>
											<Text pos="absolute" bottom={0} w="100%" ta="center" truncate="end">
												{elem.name.slice(0, elem.name.lastIndexOf('.'))}
											</Text>
										</Box>
									);
								})}
							</Flex>
						</ScrollArea.Autosize>
					}

					<FileButton
						resetRef={resetRef}
						onChange={changeHandler}
						accept="image/png,image/jpeg"
						multiple
					>
						{(props) => (
							<Button variant="default" fullWidth {...props}>
								<Group>
									<IconPaperclip />
									<Text>Добавить скриншот</Text>
								</Group>
							</Button>
						)}
					</FileButton>
				</Group>
			</form>
		</Container>
	);

	if (isMobile)
		return (
			<BottomSheet
				opened={opened}
				onClose={handleResetForm}
				title="Обращение в поддержку"
				withBackButton={false}
				buttons={buttons}
				withCloseButton
			>
				{content}
			</BottomSheet>
		);

	return (
		<ModalSheet
			title="Обращение в поддержку"
			buttons={buttons}
			opened={opened}
			onClose={close}
			centered
		>
			{content}
		</ModalSheet>
	);
};
